import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Production.css';
import { api } from '../services/db';
import Modal from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';


export default function Production() {
    const { user, logout, warehouseId, teamRoles } = useAuth();
    const navigate = useNavigate();
    const [date, setDate] = useState(() => {
        const d = new Date();
        return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    });
    const [sackType, setSackType] = useState('5');
    const [quantity, setQuantity] = useState('');
    const [prodNotes, setProdNotes] = useState(''); // New Notes field
    const [loading, setLoading] = useState(true);
    // Modal State
    const [modal, setModal] = useState({ isOpen: false, message: '', type: 'success' });
    const [isProcessing, setIsProcessing] = useState(false);
    // Pending Action for Modal
    const [pendingAction, setPendingAction] = useState(null); // 'logout'

    useEffect(() => {
        if (warehouseId) {
            loadData();
            loadStocks(); // new stock fetch
        }
        // eslint-disable-next-line
    }, [warehouseId]);

    const [dailyCount5, setDailyCount5] = useState(0);
    const [dailyCount10, setDailyCount10] = useState(0);
    const [dailyConsumed50, setDailyConsumed50] = useState(0);
    const [monthlyCount5, setMonthlyCount5] = useState(0);
    const [monthlyCount10, setMonthlyCount10] = useState(0);
    const [monthlyConsumed50, setMonthlyConsumed50] = useState(0);

    // Stock States
    const [stock5, setStock5] = useState(0);
    const [stock10, setStock10] = useState(0);
    const [stock50, setStock50] = useState(0);

    const isProductionUser = teamRoles && teamRoles.includes('production');

    async function loadData() {
        setLoading(true);
        const data = await api.getProductionRecords(warehouseId);
        updateReports(data);
        setLoading(false);
    }

    async function loadStocks() {
        try {
            const items = await api.getItems(warehouseId);

            const i5 = items.find(i => i.name === 'سكر 5 كيلو');
            const i10 = items.find(i => i.name === 'سكر 10 كيلو');
            const i50 = items.find(i => i.name === 'سكر 50 كيلو');

            if (i5) setStock5(i5.current_stock || 0);
            if (i10) setStock10(i10.current_stock || 0);
            if (i50) setStock50(i50.current_stock || 0);

        } catch (e) {
            console.error("Error loading stocks", e);
        }
    }

    function updateReports(data) {
        const d = new Date();
        const today = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        const currentMonth = today.substring(0, 7);
        let d5 = 0, d10 = 0, dConsumed = 0;
        let m5 = 0, m10 = 0, mConsumed = 0;

        data.forEach(entry => {
            // Appwrite returns sack_size and quantity
            const type = entry.sack_size;
            const count = entry.quantity;
            const consumed = entry.consumed_50kg || 0;

            if (entry.date.startsWith(today)) {
                if (type === 5) d5 += count;
                else if (type === 10) d10 += count;
                dConsumed += consumed;
            }
            if (entry.date.startsWith(currentMonth)) {
                if (type === 5) m5 += count;
                else if (type === 10) m10 += count;
                mConsumed += consumed;
            }
        });

        setDailyCount5(d5);
        setDailyCount10(d10);
        setDailyConsumed50(dConsumed);

        setMonthlyCount5(m5);
        setMonthlyCount10(m10);
        setMonthlyConsumed50(mConsumed);
    }

    // Dynamic calc for display
    const calculatedConsumed = (() => {
        const q = parseInt(quantity, 10) || 0;
        const t = parseInt(sackType, 10) || 0;
        if (q <= 0) return 0;
        const totalW = q * t;
        return Math.ceil(totalW / 50);
    })();

    async function handleSubmit(e) {
        e.preventDefault();
        const q = parseInt(quantity, 10) || 0;
        const t = parseInt(sackType, 10) || 0;
        const totalW = q * t;
        const consumed = Math.ceil(totalW / 50);

        if (prodNotes.length > 500) {
            setModal({ isOpen: true, message: 'الملاحظات يجب ألا تتجاوز 500 حرف.', type: 'error' });
            return;
        }

        setIsProcessing(true);

        try {
            const record = {
                date: date,
                sack_size: Number(sackType),
                quantity: Number(quantity),
                total_weight: totalW,
                consumed_50kg: consumed,
                prod_notes: prodNotes,
                user_name: user.name || 'Unknown'
            };

            // Atomic Function Call (4 steps in one)
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('UI_TIMEOUT')), 20000)
            );

            await Promise.race([
                api.executeCreateProduction(record, consumed, warehouseId),
                timeoutPromise
            ]);

            setModal({ isOpen: true, message: 'تم حفظ البيانات بنجاح!', type: 'success' });
            setQuantity('');
            setProdNotes('');
            loadData(); // reload reports
            loadStocks(); // reload stocks
        } catch (error) {
            let msg = error.message || 'فشل حفظ البيانات';

            if (msg === 'UI_TIMEOUT') {
                setModal({
                    isOpen: true,
                    message: 'حدث خطاء في الشبكة ، تفقد المعاملات السابقة للتأكد من نجاح العملية او فشلها قبل المحاولة مرة اخرى',
                    type: 'warning'
                });
                // Skip clean up logic below as it is specific timeout
            } else {
                // Clean up JSON-like error if present or check specifically for 50kg stock issue
                if (msg.includes('لا يوجد رصيد كافي من') && msg.includes('سكر 50 كيلو')) {
                    msg = "لا يتوفر رصيد كافي من السكر 50 كيلو لإنتاج هذه الكمية";
                }
                // Also handle if raw JSON leaked through (unlikely with db.js fix but possible)
                if (msg.includes('{"success":false')) {
                    if (msg.includes('سكر 50 كيلو')) {
                        msg = "لا يتوفر رصيد كافي من السكر 50 كيلو لإنتاج هذه الكمية";
                    }
                }
                setModal({ isOpen: true, message: msg, type: 'error' });
            }
        } finally {
            setIsProcessing(false);
        }
    }

    function closeModal() {
        if (modal.type === 'warning') {
            window.location.reload();
            return;
        }
        setModal({ ...modal, isOpen: false });
        setPendingAction(null);
    }

    // Reload on Back Button if Warning is Open
    useEffect(() => {
        const handlePopState = () => {
            if (modal.isOpen && modal.type === 'warning') {
                window.location.reload();
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [modal]);

    function handleLogoutClick() {
        setPendingAction('logout');
        setModal({ isOpen: true, message: 'هل انت متأكد من تسجيل الخروج؟', type: 'confirm' });
    }

    async function onConfirmAction() {
        if (pendingAction === 'logout') {
            await logout();
            navigate('/login');
        }
        closeModal();
    }

    return (
        <div className="production-root" dir="rtl">
            <LoadingOverlay isLoading={isProcessing} />
            <Modal isOpen={modal.isOpen} message={modal.message} type={modal.type} onClose={closeModal} onConfirm={pendingAction ? onConfirmAction : null} />

            {!isProductionUser && <Link to="/home"><button id="back-btn">رجوع</button></Link>}

            {isProductionUser && (
                <button
                    onClick={handleLogoutClick}
                    style={{
                        // position: 'absolute',
                        display: 'block',
                        top: 20,
                        left: 20,
                        background: '#e9230dff',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        // zIndex: 1000
                    }}>
                    تسجيل الخروج
                </button>
            )}

            <div className="container">
                {loading && <p style={{ textAlign: 'center', fontWeight: 'bold' }}>جاري التحميل...</p>}
                <header style={{ textAlign: 'center', marginBottom: 30 }}><h1>نظام إدارة إنتاج السكر</h1></header>
                <div className="card">
                    <h2>تسجيل إنتاج جديد</h2>
                    <form id="productionForm" onSubmit={handleSubmit}>
                        <div className="form-group"><label>تاريخ الإنتاج:</label><input type="date" value={date} readOnly required /></div>
                        <div className="form-group"><label>نوع الجوال (الحجم):</label><select value={sackType} onChange={e => setSackType(e.target.value)}><option value="5">جوال 5 كيلو</option><option value="10">جوال 10 كيلو</option></select></div>
                        <div className="form-group"><label>عدد الجوالات المنتجة:</label><input type="number" min="1" value={quantity} onChange={e => setQuantity(e.target.value)} required /></div>

                        {/* New Fields */}
                        <div className="form-group">
                            <label>جوالات 50 كيلو مستهلكة :</label>
                            <input type="text" value={calculatedConsumed} readOnly style={{ backgroundColor: '#f0f0f0', color: '#555' }} />
                        </div>

                        <div className="form-group">
                            <label>ملاحظات (اختياري - حد أقصى 500 حرف):</label>
                            <textarea
                                value={prodNotes}
                                onChange={e => setProdNotes(e.target.value)}
                                maxLength={500}
                                rows="3"
                                style={{
                                    display: 'block',
                                    width: '80%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '16px', // Bigger font
                                    resize: 'none' // No resize
                                }}
                                placeholder="اكتب ملاحظاتك هنا..."
                            ></textarea>
                            <small style={{ color: '#888' }}>المتبقي: {500 - prodNotes.length} حرف</small>
                        </div>

                        <button type="submit">حفظ البيانات</button>
                    </form>
                </div>

                <div className="card">
                    <h2>ملخص الإنتاج</h2>
                    <h3>التقرير اليومي <span id="todayDateDisplay">{new Date().toLocaleDateString('en-GB')}</span></h3>
                    <div className="dashboard-grid">
                        <div className="stat-box"><div className="stat-value">{dailyCount5}</div><div className="stat-label">جوالات 5 كيلو</div></div>
                        <div className="stat-box"><div className="stat-value">{dailyCount10}</div><div className="stat-label">جوالات 10 كيلو</div></div>
                        <div className="stat-box"><div className="stat-value">{dailyConsumed50}</div><div className="stat-label">جوالات 50 كيلو مستهلكة</div></div>
                    </div>
                    <hr />
                    <h3>التقرير الشهري <span id="currentMonthDisplay">{new Date().toISOString().slice(0, 7)}</span></h3>
                    <div className="dashboard-grid">
                        <div className="stat-box"><div className="stat-value">{monthlyCount5}</div><div className="stat-label">جوالات 5 كيلو</div></div>
                        <div className="stat-box"><div className="stat-value">{monthlyCount10}</div><div className="stat-label">جوالات 10 كيلو</div></div>
                        <div className="stat-box"><div className="stat-value">{monthlyConsumed50}</div><div className="stat-label">جوالات 50 كيلو مستهلكة</div></div>
                    </div>
                </div>

                {/* Current Stock Dashboard */}
                <div className="card" style={{ marginTop: 20 }}>
                    <h2>المخزون الحالي</h2>
                    <div className="dashboard-grid">
                        <div className="stat-box" style={{ background: '#e8f6f3' }}>
                            <div className="stat-value" style={{ color: '#16a085' }}>{stock5}</div>
                            <div className="stat-label">جوالات 5 كيلو</div>
                        </div>
                        <div className="stat-box" style={{ background: '#e8f6f3' }}>
                            <div className="stat-value" style={{ color: '#16a085' }}>{stock10}</div>
                            <div className="stat-label">جوالات 10 كيلو</div>
                        </div>
                        <div className="stat-box" style={{ background: '#fef9e7' }}>
                            <div className="stat-value" style={{ color: '#f39c12' }}>{stock50}</div>
                            <div className="stat-label">جوالات 50 كيلو</div>
                        </div>
                    </div>
                </div>

                <div className="card" style={{ textAlign: 'center' }}>
                    <Link to="/production-records">
                        <button style={{ width: '100%', fontSize: '18px' }}>سجلات الانتاج</button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
